import React from 'react';
import { Translate } from 'react-redux-i18n';
import get from 'lodash.get';
import { Calendar } from 'lucide-react';
import { ViewTable } from '../layouts/viewTable';
import { toCurrency } from '../../helpers/converterHelper';
import { getPageEntityCurrency } from './getPageEntityCurrency';
import { pageTypesStrings } from '../../constants/pageTypes';
import { filterSelectBaseOptions } from '../../constants/metadataConstants';
import { setQueryParamIfAbsent } from '../../helpers/queryHelper';
import SystemOnlyBadge, { SystemOnlyDetail } from '../common/SystemOnlyBadge';

interface FranchiseHistoryPage {
  id: number;
  type: 'fundraiser' | 'team' | 'collection';
  name: string;
  urlFull: string;
  status: string;
  raisedAmount: number;
  targetAmount: number;
  createdAtLocal: string;
  campaign: {
    id: number;
    name: string;
    mainImageUrl: string;
    urlFull: string;
    timeAndPlace?: {
      time?: {
        value?: string;
      };
      place?: {
        text?: string;
      };
    };
  };
  currency?: string;
  organization: {
    id: number;
    name: string;
    currency?: string;
  };
  franchiseEdition: {
    id: number;
    name: string;
    year: number;
  };
  franchise: {
    id: number;
    name: string;
  };
  fundraiser?: { id: number; name: string; mainImageUrl: string; urlFull: string };
  team?: { id: number; name: string; mainImageUrl: string; urlFull: string };
  collection?: { id: number; name: string; mainImageUrl: string; urlFull: string };
}

interface PageEntityFranchiseProps {
  franchiseHistory: FranchiseHistoryPage[];
  currentPageId: number | string;
  isShowEmptyFields: boolean;
  translationBase: string;
  isLoading?: boolean;
  showCurrentPageBadge?: boolean; // Flag to control showing badge vs excluding
  isSystemAdmin?: boolean;
  currentPageType: pageTypesStrings.FUNDRAISER | pageTypesStrings.TEAM | pageTypesStrings.COLLECTION;
}

interface GroupedPages {
  [year: number]: FranchiseHistoryPage[];
}

const getPageAdminUrl = (page: FranchiseHistoryPage): string => {
  switch (page.type) {
    case 'fundraiser':
      return `/fundraisers/view/${page.fundraiser?.id}`;
    case 'team':
      return `/fundraisers/team/view/${page.team?.id}`;
    case 'collection':
      return `/fundraisers/collection/view/${page.collection?.id}`;
    default:
      return '#';
  }
};

const getPageImage = (page: FranchiseHistoryPage): string => {
  // Get image from the specific page type object, or fallback to campaign
  const pageObject = page[page.type];
  return get(pageObject, 'mainImageUrl') || get(page, 'campaign.mainImageUrl') || '';
};

const getStatusColor = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (statusLower === 'live') return 'status-live';
  if (statusLower === 'completed') return 'status-completed';
  if (statusLower === 'archived') return 'status-archived';
  return 'status-completed'; // default
};

const getPageTypeLabel = (pageType: 'fundraiser' | 'team' | 'collection'): string => {
  const pageTypeOption = filterSelectBaseOptions.PageType.find(opt => opt.value === pageType);
  return pageTypeOption ? pageTypeOption.text : pageType;
};

const isCurrentPage = (
  page: FranchiseHistoryPage,
  currentPageId: number | string,
  currentPageType: pageTypesStrings.FUNDRAISER | pageTypesStrings.TEAM | pageTypesStrings.COLLECTION
): boolean => {
    // Check if the page ID matches the current page ID AND the page type matches the current page type
    const pageId = page[page.type]?.id;
    if (!pageId) return false;
    return String(pageId) === String(currentPageId) && page.type === currentPageType;
};

export const PageEntityFranchise: React.FC<PageEntityFranchiseProps> = ({
  franchiseHistory,
  currentPageId,
  isShowEmptyFields,
  translationBase,
  isLoading = false,
  showCurrentPageBadge = true,
  isSystemAdmin = false,
  currentPageType
}) => {
  // Filter and group pages by year
  const processPages = (): GroupedPages => {
    if (!franchiseHistory || !Array.isArray(franchiseHistory) || franchiseHistory.length === 0) {
      return {};
    }

    // Filter out current page if not showing badge
    let pages = franchiseHistory;
    if (!showCurrentPageBadge) {
      pages = franchiseHistory.filter(p => String(p.id) !== String(currentPageId));
    }

    // Group by year
    const grouped: GroupedPages = {};
    pages.forEach(page => {
      const year = get(page, 'franchiseEdition.year') || 0;
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(page);
    });

    return grouped;
  };

  const groupedPages = processPages();
  const years = Object.keys(groupedPages)
    .map(Number)
    .filter(y => y > 0) // Filter out any pages without a year
    .sort((a, b) => b - a); // Sort descending (most recent first)

  const franchiseName = franchiseHistory && franchiseHistory.length > 0
    ? get(franchiseHistory[0], 'franchise.name', 'Franchise')
    : 'Franchise';

  const hasAnyPages = years.length > 0;

  // Don't render if no pages and not showing empty fields
  if (!hasAnyPages && !isShowEmptyFields && !isLoading) {
    return null;
  }

  return (
    <div className="page-entity-franchise">
      <ViewTable title={`${franchiseName} History`}>
        {isLoading ? (
          <div className="franchise-history-loading">
            <Translate value="common.loading" />
          </div>
        ) : hasAnyPages ? (
          <div className="franchise-history-container">
            {years.map(year => {
              const pagesInYear = groupedPages[year];
              const pageCountKey = pagesInYear.length === 1 
                ? `${translationBase}.franchise-history.page-count-singular`
                : `${translationBase}.franchise-history.page-count-plural`;
              
              return (
                <div key={year} className="franchise-year-group">
                  {/* Year Header */}
                  <div className="franchise-year-header">
                    <Calendar className="franchise-year-icon" />
                    <span>{year}</span>
                    <span className="franchise-year-count">
                      ({pagesInYear.length} <Translate value={pageCountKey} />)
                    </span>
                  </div>

                  {/* Pages in this year */}
                  <div className="franchise-pages-list">
                    {pagesInYear.map(page => {
                      const isPageCurrent = isCurrentPage(page, currentPageId, currentPageType);
                      const imageUrl = getPageImage(page);
                      const adminUrl = getPageAdminUrl(page);
                      const percentRaised = page.targetAmount > 0
                        ? Math.min((page.raisedAmount / page.targetAmount) * 100, 100)
                        : 0;
                      const hasMetTarget = page.targetAmount > 0 && page.raisedAmount >= page.targetAmount;
                      const statusClass = getStatusColor(page.status);
                      const currency = getPageEntityCurrency(page);

                      return (
                        <div
                          key={page.id}
                          className={`franchise-page-card ${isPageCurrent && showCurrentPageBadge ? 'current-page' : ''}`}
                        >
                          <div className="franchise-page-content">
                            {/* Page Image */}
                            {imageUrl && (
                              <img
                                alt={page.name}
                                src={setQueryParamIfAbsent(imageUrl, 'w', 200)}
                                className="franchise-page-thumbnail"
                              />
                            )}

                            {/* Page Details */}
                            <div className="franchise-page-details">
                              {/* Campaign Name */}
                              <div className="franchise-campaign-header">
                                <span>
                                  {page.campaign.name}
                                  {isSystemAdmin && page.organization?.name && (
                                    <SystemOnlyDetail>
                                      {page.organization.name}
                                    </SystemOnlyDetail>
                                  )}
                                  {` - ${getPageTypeLabel(page.type)}`}
                                </span>
                                {isPageCurrent && showCurrentPageBadge && (
                                  <span className="franchise-current-badge">
                                    <Translate value={`${translationBase}.franchise-history.current-page`} />
                                  </span>
                                )}
                              </div>

                              {/* Page Name */}
                              <div className="franchise-page-name">
                                {page.urlFull ? (
                                  <a
                                    href={page.urlFull}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {page.name}
                                  </a>
                                ) : (
                                  <span>{page.name}</span>
                                )}
                              </div>

                              {/* Edition Name */}
                              {isSystemAdmin && page.franchiseEdition && (
                                <div className="franchise-edition-info">
                                  <Translate value={`${translationBase}.franchise-history.edition`} />:{' '}
                                  {page.franchiseEdition.name}
                                  <SystemOnlyBadge />
                                </div>
                              )}

                              {/* Fundraising Stats */}
                              <div className="franchise-fundraising-stats">
                                <div className="franchise-stats-row">
                                  <span className="franchise-stats-label">
                                    <Translate value={`${translationBase}.franchise-history.raised`} />:
                                  </span>
                                  <span className={`franchise-amount-raised ${hasMetTarget ? 'target-met' : ''}`}>
                                    {toCurrency(page.raisedAmount, undefined, currency)}
                                  </span>
                                  {page.targetAmount > 0 && (
                                    <>
                                      <span className="franchise-amount-separator">
                                        <Translate value={`${translationBase}.franchise-history.of`} />
                                      </span>
                                      <span>{toCurrency(page.targetAmount, undefined, currency)}</span>
                                      {hasMetTarget && (
                                        <span className="franchise-target-check">✓</span>
                                      )}
                                    </>
                                  )}
                                  <span className={`franchise-status-badge ${statusClass}`}>
                                    {page.status}
                                  </span>
                                </div>

                                {/* Progress Bar */}
                                {page.targetAmount > 0 && (
                                  <div className="franchise-progress-bar">
                                    <div
                                      className={`franchise-progress-fill ${hasMetTarget ? 'target-met' : 'in-progress'}`}
                                      style={{ width: `${percentRaised}%` }}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Actions */}
                              <div className="franchise-page-actions">
                                <a
                                  href={adminUrl}
                                >
                                  <Translate value={`${translationBase}.franchise-history.view-details`} />
                                </a>
                                {page.urlFull && (
                                  <>
                                    <span className="franchise-action-separator">|</span>
                                    <a
                                      href={page.urlFull}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <Translate value={`${translationBase}.franchise-history.visit-page`} />
                                    </a>
                                  </>
                                )}
                                {page.campaign.urlFull && (
                                  <>
                                    <span className="franchise-action-separator">|</span>
                                    <a
                                      href={page.campaign.urlFull}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <Translate value={`${translationBase}.franchise-history.view-campaign`} />
                                    </a>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {/* TODO: Add pagination controls here when implementing paging beyond 50 results */}
          </div>
        ) : (
          <div className="franchise-history-empty">
            <Translate value={`${translationBase}.franchise-history.no-history`} />
          </div>
        )}
      </ViewTable>
    </div>
  );
};

export default PageEntityFranchise;
